import { Injectable, WritableSignal, inject, signal } from "@angular/core";
import { DataService } from "@core/services/data.service";
import { firstValueFrom, Observable, take, tap, of } from "rxjs";
import { environment } from '@env/environment';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Router } from "@angular/router";
import { ILoginResponse } from "@core/interfaces/ilogin-response";
import { IForgotPwRequest, ILoginRequest } from "@core/interfaces/ilogin-request";
import { ToastService } from "./toast.service";
import { getSearchUrl } from "./helper.service";
import { IDistrict } from "@core/interfaces/ilookup";
import { LookupService } from "./lookup.service";
import { NzModalRef, NzModalService } from "ng-zorro-antd/modal";
import { HttpContext } from '@angular/common/http';
import { allowedStatuses, skipErrorToast } from "@core/interceptors/error.Interceptor";

@Injectable({
  providedIn: 'root'
})

export class AuthService {
  private readonly authToken = environment.authToken;
  private readonly userInfo = 'prism-user';
  private modal = inject(NzModalService);
  private jwtHelper = new JwtHelperService();
  currentUserInfo: WritableSignal<ILoginResponse | null> = signal(null);
  private sessionMonitorActive = false;
  private refreshThresholdMinutes = environment.refreshThresholdMinutes || 15;
  private sessionPrompted = false;
  private isLoggingOut = false;
  private sessionModalRef: NzModalRef | null = null;
  private sessionIntervalId: any = null;
  private readonly publicRoutes = [
  '/create-password',
  '/reset-password',
  '/home',
  '/about',
  '/faq',
  '/resources',
  '/resources/view',
  '/contact-us'
]; // this helps to avoid redirects even if there is an expired token that is present

  constructor(
    private dataService: DataService,
    private router: Router,
    private lookupService: LookupService,
    private toast: ToastService
  ) { this.loadUserInfo(); }

  login(payload: ILoginRequest, message?: string): Observable<any> {
    return this.dataService.post(`auth/login`, payload, message);
  }

  checkLogin(): boolean {
    const raw = localStorage.getItem(this.authToken);
    if (!raw) return false;
    const token = raw.replaceAll('"', '');
    return !this.jwtHelper.isTokenExpired(token);
  }

  refreshToken(): Observable<ILoginResponse> {
    const userInfo = this.getCurrentUser();
    const token = localStorage.getItem(this.authToken)?.replaceAll('"', '');

    const payload = {
      userId: userInfo?.userId,
      token: token,
      refreshToken: userInfo?.refreshToken
    };

    return this.dataService.post('auth/refresh-token', payload);
  }

  startSessionMonitor(): void {
    if (typeof window === 'undefined') return;
    if (this.sessionMonitorActive) return;
    this.sessionMonitorActive = true;

    this.sessionIntervalId = setInterval(() => {
      const rawToken = localStorage.getItem(this.authToken);
      if (!rawToken) return;

      const token = rawToken.replaceAll('"', '');
      const expDate = this.jwtHelper.getTokenExpirationDate(token);
      if (!expDate) return;

      const now = Date.now();
      const exp = expDate.getTime();

      if (!environment.production) {
        console.log(
          `[Session Monitor] Now: ${new Date(now).toISOString()}, Exp: ${expDate.toISOString()}`
        );
      }

      if (now >= exp) {
        console.warn('[Session Monitor] Token has expired.');

        this.sessionModalRef?.destroy();
        this.sessionModalRef = null;
        this.sessionPrompted = false;

        if (this.sessionIntervalId) {
          clearInterval(this.sessionIntervalId);
          this.sessionIntervalId = null;
          this.sessionMonitorActive = false;
        }

        this.logout(true, true);
        return;
      }

      const minutesUntilExpiry = (exp - now) / (1000 * 60);
      if (minutesUntilExpiry > 0 && minutesUntilExpiry <= this.refreshThresholdMinutes) {
        this.promptSessionExtension();
      }
    }, 10 * 1000); // check every 10s
  }


  private async promptSessionExtension(): Promise<void> {
    if (this.sessionPrompted) return;
    this.sessionPrompted = true;

    const token = localStorage.getItem(this.authToken)?.replaceAll('"', '');

    if (!token) {
      this.logout(true, true);
      this.toast.error('Session expired. Please log in again.');
      this.sessionPrompted = false;
      return;
    }

    let exp = 0;
    const expDate = this.jwtHelper.getTokenExpirationDate(token);
    if (expDate) {
      exp = expDate.getTime();
    }

    const now = Date.now();
    const timeLeftMs = exp - now;

    this.sessionModalRef = this.modal.confirm({
      nzTitle: 'Session Expiring',
      nzContent: 'Your session is about to expire. Would you like to stay signed in?',
      nzOkText: 'Yes, keep me signed in',
      nzCancelText: 'Logout',
      nzOnOk: async () => {
        try {
          const res = await firstValueFrom(this.refreshToken());

          if (res) {
            this.captureUserInfo(res);
            this.toast.success('Session extended.');
          } else {
            this.handleRefreshFailure(timeLeftMs);
          }
        } catch (err) {
          this.handleRefreshFailure(timeLeftMs);
        } finally {
          this.sessionPrompted = false;
        }
      },
      nzOnCancel: () => {
        this.logout();
        this.sessionPrompted = false;
      }
    });
  }

  private handleRefreshFailure(timeLeftMs: number): void {
    if (timeLeftMs > 0) {
      const minutes = Math.floor(timeLeftMs / 60000);
      const seconds = Math.floor((timeLeftMs % 60000) / 1000);
      this.toast.warn(`Session refresh failed. You still have ${minutes}:${seconds.toString().padStart(2, "0")} left.`);
    } else {
      this.logout(true, true);
      this.toast.error('Session expired. Please log in again.');
    }
  }

  getCurrentUser(): any {
    return JSON.parse(window.localStorage.getItem(this.userInfo) ?? '{}');
  }

  getCurrentDistrict(): IDistrict | null {
    return JSON.parse(localStorage.getItem('currentDistrict') ?? '{}');
  }

  setCurrentDistrict(district: IDistrict | null): void {
    localStorage.setItem('currentDistrict', JSON.stringify(district));
  }

  private loadUserInfo(): void {
    const storedUserInfo = localStorage.getItem(this.userInfo);
    const userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : null;
    this.currentUserInfo.set(userInfo);
  }

  captureUserInfo(loginRes: ILoginResponse): void {
    localStorage.setItem(this.authToken, JSON.stringify(loginRes.token));
    localStorage.setItem(this.userInfo, JSON.stringify(loginRes));

    const storedUserInfo = localStorage.getItem(this.userInfo);
    const userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : null;
    this.currentUserInfo.set(userInfo);

    this.startSessionMonitor();

    this.lookupService.getOrganizations().pipe(
      take(1),
      tap((organizations: IDistrict[]) => {
        localStorage.setItem('currentDistrict', JSON.stringify(organizations[0]));
        if (this.lookupService.districtSubject$.value === null && organizations.length > 0) {
          this.lookupService.districtSubject$.next(organizations[0]);
        }
      })
    ).subscribe();
  }

  // logout(isUnauthorized: boolean = false, isIdleTimeout: boolean = false): void {
  
  //   if (this.isLoggingOut) return;
  //   this.isLoggingOut = true;

  //   this.sessionModalRef?.destroy();
  //   this.sessionModalRef = null;
  //   this.sessionPrompted = false;

  //   if (this.sessionIntervalId) {
  //     clearInterval(this.sessionIntervalId);
  //     this.sessionIntervalId = null;
  //     this.sessionMonitorActive = false;
  //   }

  //   const user = JSON.parse(localStorage.getItem(this.userInfo) || '{}');
  //   const sessionId = user?.sessionId ? +user.sessionId : null;

  //   const payload = { sessionId, isIdleTimeout };

  //   this.dataService.postWithOptions(
  //     'auth/logout',
  //     { sessionId, isIdleTimeout },
  //     {
  //       context: new HttpContext()
  //         .set(skipErrorToast, true)
  //         .set(allowedStatuses, [401, 404])
  //     }
  //   )
  //   .pipe(take(1))
  //   .subscribe({
  //     next: () => this.finishLogout(isUnauthorized, isIdleTimeout),
  //     error: (err) => {
  //       if (err?.status === 401 || err?.status === 404) {
  //         this.finishLogout(true, isIdleTimeout);
  //         return;
  //       }
  //       console.warn('[Logout] Unexpected error; proceeding to clear client state:', err);
  //       this.finishLogout(isUnauthorized, isIdleTimeout);
  //     }
  //   });
  // }

  

  // private finishLogout(isUnauthorized: boolean, isIdleTimeout: boolean) {
  //   if (this.sessionIntervalId) {
  //     clearInterval(this.sessionIntervalId);
  //     this.sessionIntervalId = null;
  //   }
  //   this.sessionMonitorActive = false;
  //   this.sessionModalRef?.destroy();
  //   this.sessionModalRef = null;
  //   this.sessionPrompted = false;

  //   localStorage.clear();
  //   this.currentUserInfo.set(null);

  //   if (!isUnauthorized && !isIdleTimeout) {
  //     this.toast.success('Logged out successfully');
  //   } else if (isIdleTimeout) {
  //     this.modal.info({
  //       nzTitle: 'Session Timed Out',
  //       nzContent: 'Your session has expired due to inactivity. Please log back in to access the system.',
  //       nzOkText: 'OK',
  //       nzOnOk: () => {
  //         this.sessionModalRef?.destroy();
  //         this.sessionModalRef = null;
  //       }
  //     });
  //   }
  //   this.isLoggingOut = false;

  //   this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
  //     this.router.navigate(['/home']);
  //   });
  // }


  logout(
    isUnauthorized: boolean = false,
    isIdleTimeout: boolean = false,
    silent: boolean = false // 🔹 new flag
  ): void {

    if (this.isLoggingOut) return;
    this.isLoggingOut = true;

    if (environment.isSiteOffline || silent) {
      this.finishLogout(true, false, true); 
      // isUnauthorized = true, isIdleTimeout = false, silent = true
      return;
    }

    this.sessionModalRef?.destroy();
    this.sessionModalRef = null;
    this.sessionPrompted = false;

    if (this.sessionIntervalId) {
      clearInterval(this.sessionIntervalId);
      this.sessionIntervalId = null;
      this.sessionMonitorActive = false;
    }

    const user = JSON.parse(localStorage.getItem(this.userInfo) || '{}');
    const sessionId = user?.sessionId ? +user.sessionId : null;

    const payload = { sessionId, isIdleTimeout };

    this.dataService.postWithOptions(
      'auth/logout',
      payload,
      {
        context: new HttpContext()
          .set(skipErrorToast, true)
          .set(allowedStatuses, [401, 404])
      }
    )
      .pipe(take(1))
      .subscribe({
        next: () => this.finishLogout(isUnauthorized, isIdleTimeout, false),
        error: (err) => {
          if (err?.status === 401 || err?.status === 404) {
            this.finishLogout(true, isIdleTimeout, false);
            return;
          }
          console.warn('[Logout] Unexpected error; proceeding to clear client state:', err);
          this.finishLogout(isUnauthorized, isIdleTimeout, false);
        }
      });
  }

  private finishLogout(
    isUnauthorized: boolean,
    isIdleTimeout: boolean,
    silent: boolean = false
  ) {
    if (this.sessionIntervalId) {
      clearInterval(this.sessionIntervalId);
      this.sessionIntervalId = null;
    }
    this.sessionMonitorActive = false;
    this.sessionModalRef?.destroy();
    this.sessionModalRef = null;
    this.sessionPrompted = false;

    localStorage.clear();
    this.currentUserInfo.set(null);

    if (!silent) {
      if (!isUnauthorized && !isIdleTimeout) {
        this.toast.success('Logged out successfully');
      } else if (isIdleTimeout) {
        this.modal.info({
          nzTitle: 'Session Timed Out',
          nzContent: 'Your session has expired due to inactivity. Please log back in to access the system.',
          nzOkText: 'OK',
          nzOnOk: () => {
            this.sessionModalRef?.destroy();
            this.sessionModalRef = null;
          }
        });
      }
    }

    this.isLoggingOut = false;

    this.router.navigateByUrl('/', { replaceUrl: true }).then(() => {
      this.router.navigate(['/home']);
    });
  }

  forgotPassword(payload: IForgotPwRequest, message?: string): Observable<boolean> {
    return this.dataService.post(`auth/forgot-password`, payload, message);
  }

  isValidIdentifier(guid: string, message?: string): Observable<boolean> {
    return this.dataService.get(`auth/is-valid-identifier/${guid}`, message);
  }

  changePassword(payload: any, message?: string): Observable<boolean> {
    return this.dataService.put(`auth/password?password=${payload.password}&guid=${payload.guid}`, payload, message);
  }

  createPassword(params: any, message?: string): Observable<boolean> {
    const url = getSearchUrl('auth/create-password?', params);
    return this.dataService.put(url, message);
  }

  resetPassword(payload: { userId: number; password: string }) {
    return this.dataService.put('auth/password/admin-reset', payload);
  }

  siteVerify(token: string): Observable<boolean> {
    return this.dataService.post('auth/captcha', { token: token });
  }

  getStorageSasToken(container: string, message?: string): Observable<string> {
    return this.dataService.get(`auth/blobstorage-sas-token/${container}`, message);
  }

  fetchSasToken(resourceContainer: string, onSuccess: (sasToken: string) => void): void {
    this.getStorageSasToken(resourceContainer).pipe(
      take(1),
      tap({
        next: (res: any) => {
          const sasToken = res.value;
          onSuccess(sasToken);
        },
        error: () => {
          this.toast.error(`Failed to fetch ${resourceContainer.replace('-', ' ')}`);
        }
      })
    ).subscribe();
  }

  verifyLoginOnStart(): void {
    const storedToken = localStorage.getItem(this.authToken);
    if (!storedToken) {
      this.currentUserInfo.set(null);
      return;
    }

    const accessToken = storedToken.startsWith('"') ? JSON.parse(storedToken) : storedToken;

    const path = typeof window !== 'undefined' ? window.location.pathname : '';

    if (this.jwtHelper.isTokenExpired(accessToken)) {
      if (this.publicRoutes.some(route => path.startsWith(route))) {
        localStorage.removeItem(this.authToken);
        localStorage.removeItem('prism-user');
        this.currentUserInfo.set(null);
        return;
      }
      this.logout(true, false);
      return;
    }

    this.startSessionMonitor();
    window.addEventListener('storage', (e) => {
      if (e.key === this.authToken && !e.newValue) {
        this.logout(true, false);
      }
    });
  }
}
